import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  useTheme,
  CircularProgress
} from '@mui/material';
import { FeedPost, CreatePostData, UpdatePostData } from '@/services/OrganizationFeedService';

interface CreatePostModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreatePostData | UpdatePostData) => Promise<boolean>;
  editingPost?: FeedPost | null;
  loading?: boolean;
}

const CreatePostModal: React.FC<CreatePostModalProps> = ({
  open,
  onClose,
  onSubmit,
  editingPost,
  loading = false
}) => {
  const theme = useTheme();
  const [formData, setFormData] = useState<CreatePostData>({
    title: '',
    content: '',
    category: 'announcement',
    priority: 'normal',
    is_pinned: false,
    requires_acknowledgment: false
  });
  const [errors, setErrors] = useState<{ title?: string; content?: string }>({});

  useEffect(() => {
    if (editingPost) {
      setFormData({
        title: editingPost.title,
        content: editingPost.content,
        category: editingPost.category,
        priority: editingPost.priority,
        is_pinned: editingPost.is_pinned,
        requires_acknowledgment: editingPost.requires_acknowledgment
      });
    } else {
      setFormData({
        title: '',
        content: '',
        category: 'announcement',
        priority: 'normal',
        is_pinned: false,
        requires_acknowledgment: false
      });
    }
    setErrors({});
  }, [editingPost, open]);

  const handleChange = (field: keyof CreatePostData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = () => {
    const newErrors: { title?: string; content?: string } = {};

    if (!formData.title?.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 255) {
      newErrors.title = 'Title must be 255 characters or less';
    }

    if (!formData.content?.trim()) {
      newErrors.content = 'Content is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const success = await onSubmit(formData);
    if (success) {
      onClose();
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: `1px solid ${theme.palette.divider}`, pb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {editingPost ? 'Edit Post' : 'Create New Post'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Title */}
          <TextField
            label="Title"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            fullWidth
            required
            error={!!errors.title}
            helperText={errors.title || `${formData.title?.length || 0}/255 characters`}
            disabled={loading}
          />

          {/* Content */}
          <TextField
            label="Content"
            value={formData.content}
            onChange={(e) => handleChange('content', e.target.value)}
            fullWidth
            required
            multiline
            rows={6}
            error={!!errors.content}
            helperText={errors.content}
            disabled={loading}
            placeholder="Write your announcement, policy update, or notice here..."
          />

          {/* Category & Priority */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth disabled={loading}>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                label="Category"
                onChange={(e) => handleChange('category', e.target.value)}
              >
                <MenuItem value="announcement">Announcement</MenuItem>
                <MenuItem value="policy_update">Policy Update</MenuItem>
                <MenuItem value="general_notice">General Notice</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={loading}>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                label="Priority"
                onChange={(e) => handleChange('priority', e.target.value)}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Options */}
          <Box sx={{
            display: 'flex',
            gap: 4,
            p: 2,
            backgroundColor: theme.palette.action.hover,
            borderRadius: 2
          }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_pinned}
                  onChange={(e) => handleChange('is_pinned', e.target.checked)}
                  disabled={loading}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Pin to Top</Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    Pinned posts appear at the top of the feed
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.requires_acknowledgment}
                  onChange={(e) => handleChange('requires_acknowledgment', e.target.checked)}
                  disabled={loading}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Require Acknowledgment</Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    Employees must acknowledge reading this post
                  </Typography>
                </Box>
              }
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading && <CircularProgress size={16} color="inherit" />}
        >
          {loading ? 'Saving...' : editingPost ? 'Update Post' : 'Create Post'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePostModal;
